import axios from "axios";

export interface UserAddress {
   city: string;
   country: string;
   countryId?: string;
   line_1: string;
   line_2: string;
   postalCode: string;
   region: string;
}

export interface UserLocale {
   id: string;
   name: string;
}

export interface UserResp {
   acceptedAdminTermsAt: string;
   address: UserAddress;
   backupEmail: string | null;
   birthday: string | null;
   cohort: string | null;
   contactableByPhone: string | null;
   createdAt: string;
   createdByMsl: boolean;
   email: string;
   ethnicity: string | null;
   fullname: string | null;
   gender: string | null;
   id: string;
   informPartner: boolean;
   intercomHash: string | null;
   isAdmin: boolean;
   isRepresented: boolean;
   isSite: boolean;
   isSocialLogin: boolean;
   locale: UserLocale;
   location: string | null;
   monthlyNewsletter: boolean;
   name: string | null;
   nhsNumber: string | null;
   notifications: boolean | null;
   partner: string | null;
   phone: string | null;
   preferredContactMethodIds: string[];
   pronouns: string | null;
   referralCode: string | null;
   registrationSource: string | null;
   representedBy: string | null;
   representedByFullname: string | null;
   secondaryPhone: string | null;
   sex: string | null;
   shippingAddress: string | null;
   utmSource: string | null;
}

export interface UpdateUserFieldsPayload {
   preferred_contact_method_ids?: string[];
   locale_id?: string;
}

export default class UsersApi {
   public static get = async (): Promise<UserResp> => {
      const response = await axios.get(`/user`);
      return response?.data;
   };

   public static login = async (payload) => {
      const response = await axios.post("/x/login", payload);
      return response;
   };

   public static logout = async () => {
      const response = await axios.get("/x/logout");
      return response;
   };

   public static requestTokenRefresh = async () => {
      return await axios.get("/x/token-refresh", {
         withCredentials: true,
      });
   };

   public static updateCommsPreferencePatch = async () => {
      const response = await axios.patch(`/users/field/notifications`);
      return response;
   };

   public static updateCommsPreferenceDel = async () => {
      const response = await axios.delete(`/users/field/notifications`);
      return response;
   };

   public static updateUserFields = async (payload: UpdateUserFieldsPayload) => {
      const response = await axios.put(`/user`, payload);
      return response;
   };

   public static resetPassword = async (email) => {
      const response = await axios.post("/x/reset", { email });
      return response;
   };

   public static resetPasswordWithEmailToken = async (token: string) => {
      return await axios.post(`/x/reset/${token}`);
   };

   public static setNewPassword = async (token: string, password: string) => {
      return axios.post(
         "/x/reset/complete",
         {
            token: token,
            password: password,
            legacy_hash: false, // Use the new hashing method
         },
         { withCredentials: true }
      );
   };

   public static deleteAccount = async () => {
      const response = await axios.delete("/delete_account");
      return response;
   };

   public static getHomeFeed = async () => {
      const response = await axios.get("/user/feed", {
         withCredentials: true,
      });
      return response?.data?.data;
   };

   public static completeImpersonationAccess = async (token) => {
      const response = await axios.post(
         `/x/access/${token}`,
         {},
         {
            withCredentials: true,
         }
      );

      return response;
   };

   public static submitSignupPassword = async (token: string, password: string, agreedToSignupPolicy?: boolean) => {
      return await axios.post(
         "/x/register/complete",
         {
            token: token,
            password: password,
            // only send the agreedToSignupPolicy if it hasnt been previously accepted and the user has now accepted it
            ...(agreedToSignupPolicy && {
               has_accepted_portal_terms: agreedToSignupPolicy,
            }),
            legacy_hash: false, // Use the new hashing method
         },
         { withCredentials: true }
      );
   };

   public static validateSignupEmailToken = async (token) => {
      return await axios.post(`/x/register/${token}`, null);
   };

   public static startSignupProcess = async (params) => {
      return await axios.post(
         "/x/register",
         params // include all required params above
      );
   };

   public static submitLoginCode = async (code) => {
      return await axios.post(
         "/x/device-code",
         {
            device_code: code,
         },
         { withCredentials: true }
      );
   };
}
